import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaClipboardList, FaPlus } from "react-icons/fa6";
import { BANQUET_OWNER_ROLES, normalizeAssignedBanquetIds } from "../model/banquetOwnerModel.js";
import { createBanquetOwnerAccount, getCurrentBanquetOwner, listBanquetOwnerAccounts, logoutBanquetOwner, resetBanquetOwnerPassword, updateBanquetOwnerAccountStatus } from "../services/banquetOwnerAuthService.js";
import { getPendingBanquetSubmissions, listBanquetsForLinking, setBanquetDetailsLocked } from "../services/banquetOwnerBanquetService.js";
import GenerateOwnerLinkButton from "../components/GenerateOwnerLinkButton.jsx";

const LINK_MODES = { NEW: "new", EXISTING: "existing" };

const PENDING_REVIEW_STATUSES = ["pending_review", "pending_approval"];
const isAwaitingReview = (banquet) => PENDING_REVIEW_STATUSES.includes(banquet.status) || banquet.hasPendingEdit === true;

const inputClassName =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100";

const createEmptyFormState = () => ({
  fullName: "",
  email: "",
  phone: "",
  role: BANQUET_OWNER_ROLES.BANQUET_OWNER,
  temporaryPassword: "",
  assignedBanquetIds: "",
  notes: "",
});

export default function SuperAdminBanquetOwnersPage() {
  const [formState, setFormState] = useState(createEmptyFormState());
  const [owners, setOwners] = useState([]);
  const [status, setStatus] = useState({
    error: "",
    info: "",
    warning: "",
    isSubmitting: false,
  });
  const [resetStatus, setResetStatus] = useState({});
  const [pendingReviewCount, setPendingReviewCount] = useState(null);
  const [allBanquets, setAllBanquets] = useState([]);
  const [allBanquetsError, setAllBanquetsError] = useState("");
  const [allBanquetsSearch, setAllBanquetsSearch] = useState("");
  const [linkMode, setLinkMode] = useState(LINK_MODES.NEW);
  const [banquetSearch, setBanquetSearch] = useState("");
  const [banquetOptions, setBanquetOptions] = useState([]);
  const [banquetOptionsLoading, setBanquetOptionsLoading] = useState(false);
  const [banquetOptionsError, setBanquetOptionsError] = useState("");
  const [selectedExistingBanquet, setSelectedExistingBanquet] = useState(null);

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const loadOwners = async () => {
    const accounts = await listBanquetOwnerAccounts();
    setOwners(accounts);
  };

  const loadPendingReviewCount = async () => {
    const actingOwner = await getCurrentBanquetOwner();
    const banquets = await getPendingBanquetSubmissions({ actingOwner });
    setPendingReviewCount(banquets.filter(isAwaitingReview).length);
  };

  const loadAllBanquets = async () => {
    const actingOwner = await getCurrentBanquetOwner();
    const banquets = await listBanquetsForLinking({ actingOwner });
    setAllBanquets(banquets);
  };

  useEffect(() => {
    loadOwners().catch((error) => {
      setStatus((current) => ({
        ...current,
        error: error.message || "Unable to load banquet users.",
      }));
    });
    loadPendingReviewCount().catch(() => setPendingReviewCount(null));
    loadAllBanquets().catch((error) => setAllBanquetsError(error.message || "Unable to load the banquet list."));
  }, []);

  const filteredAllBanquets = useMemo(() => {
    const query = allBanquetsSearch.trim().toLowerCase();
    if (!query) return allBanquets;
    return allBanquets.filter((banquet) => banquet.name.toLowerCase().includes(query) || banquet.city.toLowerCase().includes(query) || banquet.id.toLowerCase().includes(query));
  }, [allBanquets, allBanquetsSearch]);

  const [lockToggleStatus, setLockToggleStatus] = useState({});

  const handleToggleDetailsLock = async (banquet) => {
    const nextLocked = !banquet.detailsLocked;
    setLockToggleStatus((current) => ({ ...current, [banquet.id]: { isSubmitting: true, error: "" } }));
    try {
      const actingOwner = await getCurrentBanquetOwner();
      await setBanquetDetailsLocked({ actingOwner, banquetId: banquet.id, detailsLocked: nextLocked });
      setAllBanquets((current) => current.map((item) => (item.id === banquet.id ? { ...item, detailsLocked: nextLocked } : item)));
      setLockToggleStatus((current) => ({ ...current, [banquet.id]: { isSubmitting: false, error: "" } }));
    } catch (error) {
      setLockToggleStatus((current) => ({ ...current, [banquet.id]: { isSubmitting: false, error: error.message || "Unable to update lock." } }));
    }
  };

  const updateField = (key, value) =>
    setFormState((current) => ({
      ...current,
      [key]: value,
    }));

  const loadBanquetOptions = async () => {
    if (banquetOptions.length || banquetOptionsLoading) {
      return;
    }

    setBanquetOptionsLoading(true);
    setBanquetOptionsError("");
    try {
      const actingOwner = await getCurrentBanquetOwner();
      const options = await listBanquetsForLinking({ actingOwner });
      setBanquetOptions(options);
    } catch (error) {
      setBanquetOptionsError(error.message || "Unable to load existing banquets.");
    } finally {
      setBanquetOptionsLoading(false);
    }
  };

  const handleSelectLinkMode = (mode) => {
    setLinkMode(mode);
    setSelectedExistingBanquet(null);
    setBanquetSearch("");
    updateField("assignedBanquetIds", "");

    if (mode === LINK_MODES.EXISTING) {
      loadBanquetOptions();
    }
  };

  const handleSelectExistingBanquet = (banquet) => {
    setSelectedExistingBanquet(banquet);
    setBanquetSearch(banquet.name);
    updateField("assignedBanquetIds", banquet.id);
  };

  const filteredBanquetOptions = useMemo(() => {
    const query = banquetSearch.trim().toLowerCase();
    if (!query) {
      return banquetOptions.slice(0, 20);
    }

    return banquetOptions
      .filter(
        (banquet) =>
          banquet.name.toLowerCase().includes(query) ||
          banquet.area.toLowerCase().includes(query) ||
          banquet.city.toLowerCase().includes(query)
      )
      .slice(0, 20);
  }, [banquetOptions, banquetSearch]);

  const handleCreateOwner = async (event) => {
    event.preventDefault();
    setStatus({
      error: "",
      info: "",
      warning: "",
      isSubmitting: true,
    });

    try {
      const assignedBanquetIds = normalizeAssignedBanquetIds({
        assignedBanquetIds: formState.assignedBanquetIds,
      });

      const payload = {
        fullName: formState.fullName.trim(),
        email: formState.email.trim().toLowerCase(),
        phone: formState.phone.trim(),
        role: formState.role,
        temporaryPassword: formState.temporaryPassword.trim(),
        assignedBanquetIds,
        notes: formState.notes.trim(),
        createdBy: "super-admin-panel",
      };

      if (!payload.fullName) {
        throw new Error("Full name is required.");
      }

      if (!payload.email) {
        throw new Error("Email is required.");
      }

      if (!payload.temporaryPassword) {
        throw new Error("Temporary password is required.");
      }

      if (linkMode === LINK_MODES.EXISTING && !selectedExistingBanquet) {
        throw new Error("Search and select the existing banquet to link this user to.");
      }

      const linkedBanquetName = selectedExistingBanquet?.name || "";
      const result = await createBanquetOwnerAccount(payload);

      setFormState(createEmptyFormState());
      setLinkMode(LINK_MODES.NEW);
      setSelectedExistingBanquet(null);
      setBanquetSearch("");
      setStatus({
        error: "",
        info: linkedBanquetName
          ? `Account created for ${result.email} (uid: ${result.uid}) and linked to existing banquet "${linkedBanquetName}". It now appears in the table below.`
          : `Account created for ${result.email} (uid: ${result.uid}). It now appears in the table below.`,
        warning: result.emailSent
          ? ""
          : `Account bana lekin welcome email nahi ja saka (${result.emailError || "unknown error"}). Manually credentials share karein: ${payload.email} / ${payload.temporaryPassword}`,
        isSubmitting: false,
      });

      await loadOwners();
    } catch (error) {
      setStatus({
        error: error.message || "Unable to create banquet owner account.",
        info: "",
        warning: "",
        isSubmitting: false,
      });
    }
  };

  const handleGeneratePassword = () => {
    const seed = formState.assignedBanquetIds || formState.fullName || formState.email.split("@")[0] || "Banquet";
    const shortName = seed
      .split(/[\s,._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .replace(/[^a-z0-9]/gi, "")
      .slice(0, 18) || "Banquet";

    updateField("temporaryPassword", `DecorZoom@${shortName}${currentYear}`);
  };

  const handleToggleActive = async (owner) => {
    setStatus((current) => ({ ...current, error: "", info: "" }));
    try {
      await updateBanquetOwnerAccountStatus({ uid: owner.uid, isActive: owner.isActive === false });
      await loadOwners();
    } catch (error) {
      setStatus((current) => ({
        ...current,
        error: error.message || "Unable to update user status.",
      }));
    }
  };

  const handleResetPasswordForOwner = async (owner) => {
    setResetStatus((current) => ({
      ...current,
      [owner.uid]: { isSubmitting: true, error: "", success: "" },
    }));

    try {
      await resetBanquetOwnerPassword({ email: owner.email });
      setResetStatus((current) => ({
        ...current,
        [owner.uid]: { isSubmitting: false, error: "", success: `Reset link sent to ${owner.email}` },
      }));
    } catch (error) {
      setResetStatus((current) => ({
        ...current,
        [owner.uid]: { isSubmitting: false, error: error.message || "Unable to send reset link.", success: "" },
      }));
    }
  };

  const handleLogout = async () => {
    try {
      await logoutBanquetOwner();
    } catch (error) {
      setStatus((current) => ({
        ...current,
        error: error.message || "Unable to logout.",
      }));
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#f5f7f4] px-3 py-6 sm:px-4 sm:py-8">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <Link to="/" className="hover:text-emerald-600">
                Home
              </Link>
              <span>/</span>
              <span className="text-slate-900">Super Admin Banquet Owners</span>
            </div>
            <h1 className="mt-3 text-2xl font-semibold text-slate-900 sm:text-3xl">Super Admin Banquet Owners</h1>
            <p className="mt-2 text-sm text-slate-600">
              Hidden admin route for creating banquet owner, manager, and staff credentials and assigning banquet IDs. No public UI entry points are exposed.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/super-admin/quick-add-banquet"
              className="inline-flex min-h-[44px] items-center gap-2 rounded-2xl border border-emerald-300 bg-white px-4 py-3 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50"
            >
              <FaPlus />
              Quick Add Banquet
            </Link>
            <Link
              to="/super-admin/banquet-review"
              className="inline-flex min-h-[44px] items-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700"
            >
              <FaClipboardList />
              Review Pending Submissions
              {typeof pendingReviewCount === "number" ? (
                <span className="inline-flex min-w-[22px] items-center justify-center rounded-full bg-white px-2 py-0.5 text-xs font-black text-emerald-700">
                  {pendingReviewCount}
                </span>
              ) : null}
              <span aria-hidden="true">&rarr;</span>
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              className="min-h-[44px] rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Logout
            </button>
          </div>
        </div>

        <section className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm sm:rounded-[28px] sm:p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-emerald-600">All Banquets</p>
          <h2 className="mt-2 text-xl font-semibold text-slate-900">Generate an owner link for any banquet</h2>
          <p className="mt-2 text-sm text-slate-600">
            Works for a brand-new banquet from Quick Add's "Create for Owner to Fill" (not live yet, fills in normally) as well as an
            already-published banquet missing details (its live listing stays untouched — the owner's edit is staged for your approval,
            same as any other profile edit, instead of overwriting what's live).
          </p>
          <input
            className={`${inputClassName} mt-4`}
            placeholder="Search by banquet name, city, or id..."
            value={allBanquetsSearch}
            onChange={(event) => setAllBanquetsSearch(event.target.value)}
          />
          {allBanquetsError ? <p className="mt-3 text-sm font-semibold text-rose-700">{allBanquetsError}</p> : null}
          <p className="mt-3 text-xs font-semibold text-slate-500">
            Showing {filteredAllBanquets.length} of {allBanquets.length} banquet(s).
          </p>
          <div className="mt-3 max-h-[420px] space-y-2 overflow-y-auto pr-1">
            {filteredAllBanquets.map((banquet) => (
              <div key={banquet.id} className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-sm font-bold text-slate-900">{banquet.name}</p>
                    <span className="rounded-full bg-white px-2.5 py-0.5 text-[11px] font-black uppercase tracking-wide text-slate-600 ring-1 ring-slate-200">
                      {banquet.status || "no status"}
                    </span>
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-[11px] font-black uppercase tracking-wide ring-1 ${
                        banquet.detailsLocked ? "bg-amber-50 text-amber-700 ring-amber-200" : "bg-emerald-50 text-emerald-700 ring-emerald-200"
                      }`}
                    >
                      {banquet.detailsLocked ? "Locked" : "Unlocked"}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">{banquet.city || "No city"} &middot; {banquet.id}</p>
                  {lockToggleStatus[banquet.id]?.error ? <p className="mt-1 text-xs font-semibold text-rose-700">{lockToggleStatus[banquet.id].error}</p> : null}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleToggleDetailsLock(banquet)}
                    disabled={lockToggleStatus[banquet.id]?.isSubmitting}
                    className="inline-flex min-h-[38px] items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {lockToggleStatus[banquet.id]?.isSubmitting ? "Updating..." : banquet.detailsLocked ? "Unlock Details" : "Lock Details"}
                  </button>
                  <GenerateOwnerLinkButton banquetId={banquet.id} label="Generate Owner Link" />
                </div>
              </div>
            ))}
            {!filteredAllBanquets.length ? <p className="rounded-xl border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500">No banquets match this search.</p> : null}
          </div>
        </section>

        <section className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm sm:rounded-[28px] sm:p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-emerald-600">Create Banquet User</p>
          <h2 className="mt-2 text-2xl font-semibold text-slate-900">Create banquet user credentials</h2>
          <p className="mt-2 text-sm text-slate-600">
            Fill in the form and click Create Banquet User. This calls a secure Cloud Function that creates the Firebase Auth account and the matching Firestore `banquetOwners` profile instantly — no terminal step needed.
          </p>

          <div className="mt-5">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Banquet for this user
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => handleSelectLinkMode(LINK_MODES.NEW)}
                className={`min-h-[48px] rounded-2xl border-2 px-4 py-3 text-sm font-semibold transition ${
                  linkMode === LINK_MODES.NEW
                    ? "border-emerald-600 bg-emerald-50 text-emerald-800"
                    : "border-slate-200 bg-white text-slate-600 hover:border-emerald-200"
                }`}
              >
                New Banquet
                <span className="mt-0.5 block text-xs font-normal text-slate-500">
                  Owner starts with a blank listing form.
                </span>
              </button>
              <button
                type="button"
                onClick={() => handleSelectLinkMode(LINK_MODES.EXISTING)}
                className={`min-h-[48px] rounded-2xl border-2 px-4 py-3 text-sm font-semibold transition ${
                  linkMode === LINK_MODES.EXISTING
                    ? "border-emerald-600 bg-emerald-50 text-emerald-800"
                    : "border-slate-200 bg-white text-slate-600 hover:border-emerald-200"
                }`}
              >
                Link to Existing Banquet
                <span className="mt-0.5 block text-xs font-normal text-slate-500">
                  Attach this user to a banquet already in the database (e.g. from bulk import) instead of creating a duplicate.
                </span>
              </button>
            </div>

            {linkMode === LINK_MODES.EXISTING ? (
              <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Search Existing Banquet
                </label>
                <input
                  className={inputClassName}
                  value={banquetSearch}
                  onChange={(event) => {
                    setBanquetSearch(event.target.value);
                    if (selectedExistingBanquet) {
                      setSelectedExistingBanquet(null);
                      updateField("assignedBanquetIds", "");
                    }
                  }}
                  placeholder="Search banquet by name..."
                  autoComplete="off"
                />

                {banquetOptionsLoading ? (
                  <p className="mt-2 text-xs font-medium text-slate-500">Loading banquets...</p>
                ) : null}
                {banquetOptionsError ? (
                  <p className="mt-2 text-xs font-medium text-rose-600">{banquetOptionsError}</p>
                ) : null}

                {selectedExistingBanquet ? (
                  <div className="mt-2 flex items-start justify-between gap-3 rounded-xl border border-emerald-200 bg-white p-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{selectedExistingBanquet.name}</p>
                      <p className="text-xs text-slate-500">
                        {[selectedExistingBanquet.area, selectedExistingBanquet.city].filter(Boolean).join(", ") || "No area/city on file"}
                        {" · "}
                        {selectedExistingBanquet.id}
                      </p>
                      {selectedExistingBanquet.ownerId ? (
                        <p className="mt-1 text-xs font-semibold text-amber-700">
                          Already has an owner ({selectedExistingBanquet.ownerEmail || selectedExistingBanquet.ownerId}). Creating this user will replace that owner assignment.
                        </p>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedExistingBanquet(null);
                        setBanquetSearch("");
                        updateField("assignedBanquetIds", "");
                      }}
                      className="shrink-0 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Change
                    </button>
                  </div>
                ) : !banquetOptionsLoading && !banquetOptionsError ? (
                  <div className="mt-2 max-h-64 space-y-1 overflow-y-auto">
                    {filteredBanquetOptions.map((banquet) => (
                      <button
                        key={banquet.id}
                        type="button"
                        onClick={() => handleSelectExistingBanquet(banquet)}
                        className="block w-full rounded-xl border border-transparent px-3 py-2 text-left text-sm hover:border-emerald-200 hover:bg-emerald-50"
                      >
                        <span className="block font-semibold text-slate-900">{banquet.name}</span>
                        <span className="block text-xs text-slate-500">
                          {[banquet.area, banquet.city].filter(Boolean).join(", ") || "No area/city on file"}
                          {" · "}
                          {banquet.status || "no status"}
                        </span>
                      </button>
                    ))}
                    {!filteredBanquetOptions.length ? (
                      <p className="px-3 py-2 text-xs font-medium text-slate-500">No banquets match this search.</p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <form className="mt-6 grid gap-4 md:grid-cols-2" onSubmit={handleCreateOwner}>
            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Full Name
              </label>
              <input
                className={inputClassName}
                value={formState.fullName}
                onChange={(event) => updateField("fullName", event.target.value)}
                placeholder="Banquet owner name"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Email
              </label>
              <input
                className={inputClassName}
                type="email"
                value={formState.email}
                onChange={(event) => updateField("email", event.target.value)}
                placeholder="owner@banquet.com"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Phone
              </label>
              <input
                className={inputClassName}
                value={formState.phone}
                onChange={(event) => updateField("phone", event.target.value)}
                placeholder="Phone number"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Role
              </label>
              <select
                className={inputClassName}
                value={formState.role}
                onChange={(event) => updateField("role", event.target.value)}
              >
                <option value={BANQUET_OWNER_ROLES.BANQUET_OWNER}>banquet_owner</option>
                <option value={BANQUET_OWNER_ROLES.BANQUET_MANAGER}>banquet_manager</option>
                <option value={BANQUET_OWNER_ROLES.BANQUET_STAFF}>banquet_staff</option>
              </select>
            </div>

            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Temporary Password
              </label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  className={inputClassName}
                  type="password"
                  value={formState.temporaryPassword}
                  onChange={(event) => updateField("temporaryPassword", event.target.value)}
                  placeholder="Temporary password"
                />
                <button
                  type="button"
                  onClick={handleGeneratePassword}
                  className="min-h-[44px] rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800"
                >
                  Generate
                </button>
              </div>
            </div>

            {linkMode === LINK_MODES.NEW ? (
              <div className="md:col-span-2">
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Assigned Banquet IDs
                </label>
                <input
                  className={inputClassName}
                  value={formState.assignedBanquetIds}
                  onChange={(event) => updateField("assignedBanquetIds", event.target.value)}
                  placeholder="banquet-id-1, banquet-id-2"
                />
                <p className="mt-1 text-xs text-slate-500">
                  Optional — for assigning a manager/staff to one or more existing banquet IDs directly. Leave blank for a fresh banquet_owner.
                </p>
              </div>
            ) : null}

            <div className="md:col-span-2">
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Notes
              </label>
              <input
                className={inputClassName}
                value={formState.notes}
                onChange={(event) => updateField("notes", event.target.value)}
                placeholder="Optional internal notes"
              />
            </div>

            {status.error ? (
              <div className="md:col-span-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {status.error}
              </div>
            ) : null}

            {status.info ? (
              <div className="md:col-span-2 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-700">
                {status.info}
              </div>
            ) : null}

            {status.warning ? (
              <div className="md:col-span-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {status.warning}
              </div>
            ) : null}

            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={status.isSubmitting}
                className="min-h-[44px] w-full rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
              >
                {status.isSubmitting ? "Creating Account..." : "Create Banquet User"}
              </button>
            </div>
          </form>
        </section>

        <section className="mt-6 rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm sm:rounded-[28px] sm:p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-emerald-600">Users</p>
          <h2 className="mt-2 text-2xl font-semibold text-slate-900">Banquet account assignments</h2>
          <div className="mt-5 overflow-x-auto pb-2">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs uppercase tracking-[0.16em] text-slate-500">
                <tr>
                  <th className="py-3 pr-4">User</th>
                  <th className="py-3 pr-4">Role</th>
                  <th className="py-3 pr-4">Assigned Banquets</th>
                  <th className="py-3 pr-4">Status</th>
                  <th className="py-3 pr-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {owners.map((owner) => (
                  <tr key={owner.uid} className="border-b border-slate-100 align-top">
                    <td className="py-4 pr-4">
                      <div className="font-semibold text-slate-900">{owner.displayName || owner.fullName || owner.email}</div>
                      <div className="mt-1 text-xs text-slate-500">{owner.email}</div>
                    </td>
                    <td className="py-4 pr-4 text-slate-700">{owner.role}</td>
                    <td className="py-4 pr-4 text-slate-700">
                      <span
                        className={`mb-1 inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ${
                          owner.assignedBanquetIds.length ? "bg-sky-50 text-sky-700" : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {owner.assignedBanquetIds.length ? "Linked" : "New"}
                      </span>
                      <div>{owner.assignedBanquetIds.join(", ") || "-"}</div>
                    </td>
                    <td className="py-4 pr-4">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${owner.isActive === false ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>
                        {owner.isActive === false ? "Inactive" : "Active"}
                      </span>
                    </td>
                    <td className="py-4 pr-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleToggleActive(owner)}
                          className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700"
                        >
                          {owner.isActive === false ? "Activate" : "Deactivate"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleResetPasswordForOwner(owner)}
                          disabled={resetStatus[owner.uid]?.isSubmitting}
                          className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {resetStatus[owner.uid]?.isSubmitting ? "Sending..." : "Reset Password"}
                        </button>
                      </div>
                      {resetStatus[owner.uid]?.success ? (
                        <p className="mt-1.5 max-w-[220px] text-xs font-semibold text-emerald-700">{resetStatus[owner.uid].success}</p>
                      ) : null}
                      {resetStatus[owner.uid]?.error ? (
                        <p className="mt-1.5 max-w-[220px] text-xs font-semibold text-rose-700">{resetStatus[owner.uid].error}</p>
                      ) : null}
                    </td>
                  </tr>
                ))}
                {!owners.length ? (
                  <tr>
                    <td className="py-5 text-sm text-slate-500" colSpan={5}>No banquet users found.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
